import { useMemo, useState } from 'react';
import { type EntitySuggestion, type System } from '@bliks-io/contracts';
import { EntityPickerModal } from '@bliks-io/ui';
import {
  useSystemPickerConfig,
  type SystemPickerRequestContext
} from '../hooks/useSystemPickerConfig';
import { suggestionPickId } from '../shared/suggestionPickEvent';

export interface SystemPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'single' | 'multi';
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  excludeIds?: string[];
  onBack?: () => void;
  /** Process the picker is opened from. Drives the "for this process" bucket. */
  processId?: string;
  /** Current recording — excluded from the "for this process" bucket aggregate. */
  recordingId?: string;
  /**
   * Carries the recording context the suggest-new dialog needs to attach the
   * created `EntitySuggestion` to the right slot. Without it, the picker hides
   * the suggest-new affordance.
   */
  requestContext?: SystemPickerRequestContext;
  /** Draft-editor host: create/link post to the draft suggestion routes. */
  draftProcessId?: string;
  /** Fired after a suggestion is created or adopted; consumer appends the
   *  ref to its slot (a pure slot-writer — it never tracks withdraws, so the
   *  created/adopted distinction is collapsed to the id here). */
  onSuggestionCreated?: (suggestionId: string) => void;
  /**
   * Pending system suggestions to surface as a read-only ghost section in the
   * picker's empty-query view. Caller pre-filters to `kind === 'system'`.
   */
  pendingSuggestions?: EntitySuggestion[];
  /** Editor opt-in: create the system directly instead of suggesting it. */
  allowCreate?: boolean;
}

/**
 * `EntityPickerModal` paired with the system config hook's suggestion plumbing
 * (the suggest-new dialog). PersonPicker and ExternalContactPicker don't carry
 * the suggest-new flow, so this stays system-specific.
 */
export function SystemPickerModal({
  open,
  onOpenChange,
  mode,
  selectedIds,
  onConfirm,
  excludeIds,
  onBack,
  processId,
  recordingId,
  draftProcessId,
  requestContext,
  onSuggestionCreated,
  pendingSuggestions,
  allowCreate
}: SystemPickerModalProps) {
  const { items, isLoading, mergedSelectedIds, config, suggestNew, slotMode, renderSuggestDialog } =
    useSystemPickerConfig({
      open,
      mode,
      processId,
      recordingId,
      draftProcessId,
      requestContext,
      selectedIds,
      pendingSuggestions,
      allowCreate
    });

  // Systems created from the picker join the tentative draft instead of
  // confirming for the user — the modal outlives close, so they are dropped on
  // every open transition. `EntityPickerModal` merges prop-driven additions
  // into its draft, which is why they ride in through `selectedIds`.
  const [createdIds, setCreatedIds] = useState<string[]>([]);
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    setCreatedIds([]);
  }
  const draftSelectedIds = useMemo(
    () => (createdIds.length > 0 ? [...mergedSelectedIds, ...createdIds] : mergedSelectedIds),
    [mergedSelectedIds, createdIds]
  );

  return (
    <>
      <EntityPickerModal<System>
        open={open}
        onOpenChange={onOpenChange}
        items={items}
        isLoading={isLoading}
        selectedIds={draftSelectedIds}
        mode={mode}
        onConfirm={onConfirm}
        config={config}
        suggestNew={suggestNew}
        onBack={onBack}
        excludeIds={excludeIds}
      />
      {renderSuggestDialog({
        onUseExistingEntity: (next) => {
          if (slotMode === 'create') {
            const additions = next.filter((id) => !mergedSelectedIds.includes(id));
            setCreatedIds((ids) => [...new Set([...ids, ...additions])]);
            return;
          }
          onConfirm(next);
          onOpenChange(false);
        },
        onSuggestionCreated: onSuggestionCreated
          ? (event) => {
              onSuggestionCreated(suggestionPickId(event));
              onOpenChange(false);
            }
          : undefined
      })}
    </>
  );
}
